import { addMinutes, differenceInMinutes, format, isAfter, isBefore } from 'date-fns/esm';
import { TFunction } from 'i18next';
import { Option } from 'slim-select/dist/data.d';

import { getDurationInMinutes, getTimeDate } from '../../core/utils/date';
import { DATE_FORMAT_HOURS_MINUTES } from '../../core/constants/date';
import Booking from '../../core/types/model/Booking';
import { BookingColumnLayout, BookingPlacement, TimeSlotDefinition } from './types';
import { GlobalNotificationTypes } from '../../core/enums/GlobalNotificationTypes';
import Equipment from '../../core/types/model/Equipment';
import { Contact } from '../../core/state/subcontractors/types';

const TIME_SLOT_DIVISIONS = 10; // Number of cards that can fit horizontally
const MIN_DURATION = 30;

export const generateTimeSlotDefinitions = (operatingHoursFrom: string, operatingHoursTo: string) => {
  const startTime = getTimeDate(operatingHoursFrom);
  const endTime = getTimeDate(operatingHoursTo);
  let timeCounter = new Date(startTime);

  const defs = [] as TimeSlotDefinition[];

  let gridRowIndex = 1; // grid positions start from 1

  if (timeCounter.getMinutes() === 30) {
    defs.push({ index: gridRowIndex, rowSpan: 1, startTime: format(timeCounter, DATE_FORMAT_HOURS_MINUTES), onHour: false });
    timeCounter = addMinutes(timeCounter, 30);
    gridRowIndex += 1;
  }

  while (differenceInMinutes(endTime, timeCounter) >= 60) {
    defs.push({ index: gridRowIndex, rowSpan: 2, startTime: format(timeCounter, DATE_FORMAT_HOURS_MINUTES), onHour: true });
    timeCounter = addMinutes(timeCounter, 60);
    gridRowIndex += 2;
  }

  if (differenceInMinutes(endTime, timeCounter) === 30) {
    defs.push({
      index: gridRowIndex,
      rowSpan: 1,
      startTime: format(timeCounter, DATE_FORMAT_HOURS_MINUTES),
      onHour: timeCounter.getMinutes() === 0
    });
    timeCounter = addMinutes(timeCounter, 30);
  }

  if (differenceInMinutes(endTime, timeCounter) !== 0) {
    throw new Error(`Invalid operating hours: ${operatingHoursFrom} - ${operatingHoursTo}`);
  }

  return defs;
};

const calculateTimeSlotIndex = (timeString: string, startTime: Date): number => {
  const difference = differenceInMinutes(getTimeDate(timeString), startTime);
  return difference / MIN_DURATION;
};

export const placeBookings = (bookings: Booking[], operatingHoursFrom: string, operatingHoursTo: string): BookingPlacement => {
  const startTime = getTimeDate(operatingHoursFrom);
  const endTime = getTimeDate(operatingHoursTo);
  const durationInMinutes = getDurationInMinutes(operatingHoursTo, operatingHoursFrom);
  const numTimeSlots = durationInMinutes / 30;

  // Remove bookings outside time range
  const filteredBookings = bookings.filter(
    (booking) => !isBefore(getTimeDate(booking.startTime), startTime) && !isAfter(getTimeDate(booking.endTime), endTime)
  );

  // Create empty placement grid
  const bookingGrid: number[][] = []; // Booking ID is value
  for (let i = 0; i < numTimeSlots; i += 1) {
    bookingGrid.push(Array(TIME_SLOT_DIVISIONS));
  }

  // Place bookings in grid with width of 1 column
  const columnLayouts: BookingColumnLayout[] = [];
  const overflowRows = Array(numTimeSlots).fill(0) as number[];

  const isRowFull = (row: number[]): boolean => {
    // Check if there is any free slot in the row
    for (let i = 0; i < TIME_SLOT_DIVISIONS; i += 1) {
      if (!row[i]) return false;
    }
    return true;
  };

  filteredBookings.forEach((booking) => {
    const startIndex = calculateTimeSlotIndex(booking.startTime, startTime);
    const endIndex = calculateTimeSlotIndex(booking.endTime, startTime);

    // Check booking will fit in available slots
    let columnIndex = 0;
    // Find start column
    const startRow = bookingGrid[startIndex];
    for (; columnIndex < TIME_SLOT_DIVISIONS; columnIndex += 1) {
      if (!startRow[columnIndex]) break;
    }
    // No free column available in the start time slot
    if (columnIndex >= TIME_SLOT_DIVISIONS) {
      for (let rowIndex = startIndex; rowIndex < endIndex; rowIndex += 1) {
        if (bookingGrid[rowIndex] && isRowFull(bookingGrid[rowIndex])) {
          overflowRows[rowIndex] += 1;
        }
      }
      return;
    }

    // Search vertically downwards
    for (let rowIndex = startIndex; rowIndex < endIndex; rowIndex += 1) {
      // If a cell is filled start over with next column
      if (bookingGrid[rowIndex][columnIndex]) {
        rowIndex = startIndex;
        columnIndex += 1;
      }
    }

    // Place the booking
    if (columnIndex >= TIME_SLOT_DIVISIONS) {
      for (let rowIndex = startIndex; rowIndex < endIndex; rowIndex += 1) {
        if (bookingGrid[rowIndex] && isRowFull(bookingGrid[rowIndex])) {
          overflowRows[rowIndex] += 1;
        }
      }
      return;
    }

    for (let rowIndex = startIndex; rowIndex < endIndex; rowIndex += 1) {
      bookingGrid[rowIndex][columnIndex] = booking.id;
    }
    columnLayouts.push({
      booking,
      rowStart: startIndex + 1,
      rowEnd: endIndex + 1,
      columnStart: columnIndex + 1,
      columnEnd: columnIndex + 2,
      expanded: false,
      halfHeight: endIndex - startIndex === 1,
      concurrentBookingIds: new Set<number>()
    });
  });

  // Populate concurrent bookings
  // Precompute this for the inner loop below
  const layoutsByBookingId = columnLayouts.reduce((layoutIndexes, layout) => {
    return { ...layoutIndexes, [layout.booking.id]: layout };
  }, {} as Record<number, BookingColumnLayout>);

  columnLayouts.forEach((layout) => {
    for (let rowIndex = layout.rowStart - 1; rowIndex <= layout.rowEnd - 2; rowIndex += 1) {
      for (let columnIndex = layout.columnEnd - 1; columnIndex < TIME_SLOT_DIVISIONS; columnIndex += 1) {
        if (bookingGrid[rowIndex] && bookingGrid[rowIndex][columnIndex]) {
          layout.hasRightWiseBooking = true; // eslint-disable-line no-param-reassign

          const otherBookingId = bookingGrid[rowIndex][columnIndex];
          layout.concurrentBookingIds.add(otherBookingId);
          layoutsByBookingId[otherBookingId].concurrentBookingIds.add(layout.booking.id);
        }
      }
    }
  });

  const hasOnlyOneRightWiseConcurrentBooking = (layout: BookingColumnLayout) => {
    let isOnlyOneInLine = true;
    const secondItemHasRightWiseBooking = !layoutsByBookingId[layout.concurrentBookingIds.values().next().value].hasRightWiseBooking;
    if (layout.concurrentBookingIds.size === 1 && secondItemHasRightWiseBooking) {
      return true;
    }
    layout.concurrentBookingIds.forEach((item) => {
      const concurrentBooking = layoutsByBookingId[item];

      if (concurrentBooking.hasRightWiseBooking) {
        isOnlyOneInLine = false;
      }
    });

    return isOnlyOneInLine;
  };

  // Expand bookings on grid to fill width
  const layouts = columnLayouts.map((layout) => {
    if (layout.expanded || layout.hasRightWiseBooking) {
      if (hasOnlyOneRightWiseConcurrentBooking(layout)) {
        const columnEnd = Array.from(layout.concurrentBookingIds).reduce((nearestColumnStart, bookingId) => {
          const concurrentLayout = layoutsByBookingId[bookingId];
          const isCoveredByLongerBooking =
            concurrentLayout && (concurrentLayout.rowStart < layout.rowStart || concurrentLayout.rowEnd > layout.rowEnd);

          if (!isCoveredByLongerBooking || concurrentLayout.columnStart <= layout.columnStart) {
            return nearestColumnStart;
          }
          return Math.min(nearestColumnStart, concurrentLayout.columnStart);
        }, TIME_SLOT_DIVISIONS / 2 + 1);

        return {
          ...layout,
          columnStart: 1,
          columnEnd,
          expanded: true,
          halfWidth: columnEnd === TIME_SLOT_DIVISIONS / 2 + 1
        };
      }
      return layout;
    }

    if (layout.columnStart === 2 && !layout.hasRightWiseBooking) {
      return {
        ...layout,
        columnStart: TIME_SLOT_DIVISIONS / 2 + 1,
        columnEnd: TIME_SLOT_DIVISIONS + 1,
        expanded: true,
        halfWidth: true
      };
    }

    // Two bookings half width
    if (layout.concurrentBookingIds.size === 1) {
      // Check that the other booking overlaps with only this one
      const concurrentBookingId = layout.concurrentBookingIds.values().next().value;
      const concurrentLayout = layoutsByBookingId[concurrentBookingId];

      if (concurrentLayout.concurrentBookingIds.size === 1) {
        concurrentLayout.columnEnd = TIME_SLOT_DIVISIONS / 2 + 1;
        concurrentLayout.expanded = true;

        return {
          ...layout,
          columnStart: TIME_SLOT_DIVISIONS / 2 + 1,
          columnEnd: TIME_SLOT_DIVISIONS + 1,
          expanded: true,
          halfWidth: true
        };
      }
    }

    // Fill all remaining space
    return {
      ...layout,
      columnEnd: TIME_SLOT_DIVISIONS + 1,
      expanded: true
    };
  });

  // Prevent expansion in last column (overflow handling)
  const layoutsWithOverflowAdjustments = layouts.map((layout) => {
    if (layout.columnStart !== 10) {
      return layout;
    }
    return {
      ...layout,
      expanded: false,
      halfWidth: false
    };
  });
  return { layouts: layoutsWithOverflowAdjustments, overflowRows };
};
export const numGridSlots = (operatingHoursFrom: string, operatingHoursTo: string): number => {
  const difference = differenceInMinutes(getTimeDate(operatingHoursTo), getTimeDate(operatingHoursFrom));
  return difference / MIN_DURATION;
};

export type SubmitAction = 'approve' | 'reject' | 'cancel' | 'done' | 'open';

export const getBookingNotification = (submitAction: SubmitAction, t: TFunction) => {
  switch (submitAction) {
    case 'approve':
      return { type: GlobalNotificationTypes.Success, message: t('booking.approve.notification') };
    case 'reject':
      return { type: GlobalNotificationTypes.Info, message: t('booking.reject.notification'), title: t('booking.reject.notification.title') };
    case 'cancel':
      return { type: GlobalNotificationTypes.Success, message: t('booking.cancel.notification') };
    case 'done':
      return { type: GlobalNotificationTypes.Success, message: t('booking.done.notification') };
    case 'open':
      return { type: GlobalNotificationTypes.Success, message: t('booking.open.notification') };
    default:
      return null;
  }
};

export const equipmentToOption = (equipment: Equipment): Option => ({
  value: equipment.id.toString(),
  text: equipment.name
});

export const unloadingTimeToOptions = (min: number, max: number): Option[] => {
  const unloadingTimes: Option[] = [];
  for (let i = min; i <= max; i += 0.5) {
    unloadingTimes.push({
      value: i.toString(),
      text: `${i.toString()} h`
    });
  }
  return unloadingTimes;
};

export const contactsToOptions = (contact: Contact): Option => ({
  text: `${contact.firstName} ${contact.lastName} ${contact.phoneNumber ? `(${contact.phoneNumber})` : ''}`,
  value: `${contact.id}`
});
